import { useState, useRef } from 'react';
import { 
  Camera, 
  X, 
  Star, 
  Loader2, 
  Plus,
  Trash2,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function PhotoGallery({ photos = [], profilePhoto, onUpdate, editable = true }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large. Maximum 5MB allowed.');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        
        const token = localStorage.getItem('dharmbook_token');
        const formData = new FormData();
        formData.append('photo_data', base64);
        formData.append('is_profile_photo', photos.length === 0 ? 'true' : 'false');

        const response = await axios.post(
          `${API}/photos/upload?token=${token}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Photo uploaded successfully!');
        onUpdate?.();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (index) => {
    if (!confirm('Delete this photo?')) return;
    
    setDeleting(index);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/photos/${index}?token=${token}`);
      toast.success('Photo deleted');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setDeleting(null);
    }
  };

  const handleSetProfilePhoto = async (index) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/photos/set-profile/${index}?token=${token}`);
      toast.success('Profile photo updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update profile photo');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Photos</h3>
        <span className="text-sm text-gray-500">{photos.length}/5 photos</span>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {/* Existing photos */}
        {photos.map((photo, index) => (
          <div 
            key={index} 
            className="relative aspect-square rounded-xl overflow-hidden group bg-gray-100"
          >
            <img 
              src={photo} 
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Profile photo badge */}
            {photo === profilePhoto && (
              <div className="absolute top-1 left-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star className="w-3 h-3" fill="white" />
                <span>Main</span>
              </div>
            )}

            {/* Actions overlay */}
            {editable && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {photo !== profilePhoto && (
                  <button
                    onClick={() => handleSetProfilePhoto(index)}
                    className="p-2 bg-white rounded-full hover:bg-amber-50 transition-colors"
                    title="Set as profile photo"
                  >
                    <Star className="w-4 h-4 text-amber-600" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(index)}
                  disabled={deleting === index}
                  className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                  title="Delete photo"
                >
                  {deleting === index ? (
                    <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 text-red-600" />
                  )}
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add photo button */}
        {editable && photos.length < 5 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-rose-400 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-rose-500 transition-colors bg-gray-50 hover:bg-rose-50"
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <>
                <Plus className="w-8 h-8" />
                <span className="text-xs font-medium">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Guidelines */}
      <div className="bg-amber-50 rounded-xl p-4 text-sm">
        <p className="font-medium text-amber-800 mb-2">Photo Guidelines:</p>
        <ul className="text-amber-700 text-xs space-y-1">
          <li>• Upload clear, recent photos with face clearly visible</li>
          <li>• Avoid group photos as main profile photo</li>
          <li>• Maximum 5 photos, each up to 5MB</li>
          <li>• The first photo will be your profile photo</li>
        </ul>
      </div>
    </div>
  );
}
